import {
  ArrowRight,
  ChevronRight,
  CircleAlert,
  Eye,
  ShieldCheck,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import type { BookingChecksRepository } from '../../booking-checks/booking-checks-repository'
import { BookingCheckCard } from '../../booking-checks/components/BookingCheckCard'
import {
  useBookingChecks,
  useResolveBookingCheck,
} from '../../booking-checks/use-booking-checks'
import type { ConversationAttentionItem } from '../../shared/conversation-attention'
import type { OverviewRepository } from '../overview-repository'
import { useOverviewAttention } from '../use-overview-operations'
import { Card } from '../../../ui/Card/Card'
import { StatePanel } from '../../../ui/StatePanel/StatePanel'
import { requestSummary } from '../../conversations/action-request-presentation'
import styles from '../OverviewPage.module.css'

// The same status and wording Conversations shows for each conversation.
function itemCopy(item: ConversationAttentionItem) {
  return item.attention === 'NEEDS_ACTION'
    ? {
        label: 'Needs action',
        detail: requestSummary(
          item.unresolvedActionRequestTypes,
          item.unresolvedActionRequestCount
        ),
      }
    : { label: 'Needs review', detail: 'New activity' }
}

function relativeTimestamp(timestamp: number) {
  const deltaSeconds = Math.round((timestamp - Date.now()) / 1000)
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  if (Math.abs(deltaSeconds) < 60)
    return formatter.format(deltaSeconds, 'second')
  const minutes = Math.round(deltaSeconds / 60)
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute')
  const hours = Math.round(minutes / 60)
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour')
  return formatter.format(Math.round(hours / 24), 'day')
}

export function NeedsAttention({
  tenantId,
  repository,
  bookingChecksRepository = null,
}: {
  tenantId: string
  repository: OverviewRepository
  bookingChecksRepository?: BookingChecksRepository | null
}) {
  const navigate = useNavigate()
  const attention = useOverviewAttention(repository, tenantId)
  const value = attention.data
  const bookingChecks = useBookingChecks(bookingChecksRepository, tenantId)
  const resolveCheck = useResolveBookingCheck(bookingChecksRepository, tenantId)
  // Bookings not yet seen in the practice system come first: a patient may
  // arrive for them.
  const openChecks = (bookingChecks.data?.items ?? []).filter(
    (check) => check.status === 'OPEN'
  )

  return (
    <Card className={`${styles.panel} ${styles.attentionPanel}`}>
      <header className={styles.panelHeader}>
        <div>
          <span className={styles.sectionLabel}>Needs a person</span>
          <h3>Needs attention</h3>
        </div>
        {value ? (
          <span
            className={styles.attentionCount}
            aria-label={`${value.outstandingCount} ${value.outstandingCount === 1 ? 'conversation needs' : 'conversations need'} attention`}
          >
            {value.outstandingCount > 99 ? '99+' : value.outstandingCount}
          </span>
        ) : null}
      </header>
      {attention.isPending ? (
        <div
          className={styles.rowsLoading}
          aria-label="Loading attention items"
        >
          {[0, 1, 2].map((row) => (
            <span key={row} />
          ))}
        </div>
      ) : attention.isError ? (
        <StatePanel
          kind="error"
          title="Attention unavailable"
          description="The conversations that need staff could not be loaded."
        />
      ) : null}
      {bookingChecks.isError ? (
        <StatePanel
          kind="error"
          title="Booking checks unavailable"
          description="Bookings to check in your practice system couldn’t be loaded."
        />
      ) : null}
      {openChecks.length > 0 ? (
        <ul
          className={styles.bookingCheckList}
          aria-label="Bookings to check in the practice system"
        >
          {openChecks.map((check) => (
            <li key={check.bookingCheckId}>
              <BookingCheckCard
                check={check}
                onResolve={(resolutionType) =>
                  resolveCheck.mutate({ check, resolutionType })
                }
                pending={
                  resolveCheck.isPending &&
                  resolveCheck.variables?.check.bookingCheckId ===
                    check.bookingCheckId
                    ? (resolveCheck.variables?.resolutionType ?? null)
                    : null
                }
                failed={
                  resolveCheck.isError &&
                  resolveCheck.variables?.check.bookingCheckId ===
                    check.bookingCheckId
                }
              />
            </li>
          ))}
        </ul>
      ) : null}
      {attention.isPending || attention.isError ? null : value &&
        value.items.length === 0 &&
        openChecks.length === 0 &&
        !bookingChecks.isError ? (
        <div className={styles.quietState}>
          <ShieldCheck size={22} aria-hidden="true" />
          <strong>No conversations need attention</strong>
          <p>Every request is completed and every conversation reviewed.</p>
        </div>
      ) : value && value.items.length > 0 ? (
        <>
          <ul className={styles.railList}>
            {value.items.map((item) => {
              const copy = itemCopy(item)
              const Icon = item.attention === 'NEEDS_ACTION' ? CircleAlert : Eye
              return (
                <li key={item.aggregateId}>
                  <button
                    type="button"
                    onClick={() =>
                      void navigate(
                        `/conversations?aggregate=${encodeURIComponent(item.aggregateId)}`
                      )
                    }
                  >
                    <span
                      className={
                        item.attention === 'NEEDS_ACTION'
                          ? styles.railIcon
                          : `${styles.railIcon} ${styles.reviewIcon}`
                      }
                      aria-hidden="true"
                    >
                      <Icon size={15} />
                    </span>
                    <span className={styles.railCopy}>
                      <strong>{copy.label}</strong>
                      <span>
                        {copy.detail} · {relativeTimestamp(item.activityAtMs)}
                      </span>
                    </span>
                    <ChevronRight size={15} aria-hidden="true" />
                  </button>
                </li>
              )
            })}
          </ul>
          <footer className={styles.panelFooter}>
            <div className={styles.panelFooterRow}>
              <button
                type="button"
                className={styles.panelAction}
                onClick={() => void navigate('/conversations')}
              >
                View conversations <ArrowRight size={14} aria-hidden="true" />
              </button>
            </div>
          </footer>
        </>
      ) : null}
    </Card>
  )
}
